/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import {
  AuthStateResult,
  LoginResponse,
  OidcSecurityService,
  UserDataResult,
  ValidationResult,
} from 'angular-auth-oidc-client';
import { BehaviorSubject, Observable } from 'rxjs';

interface KeycloakTokenDecoded {
  exp: number;
  iat: number;
  auth_time: number;
  jti: string;
  iss: string;
  aud: string;
  sub: string;
  typ: string;
  nonce: string;
  session_state: string;
  acr: string;
  'allowed-origins': string[];
  scope: string;
  sid: string;
  email_verified: boolean;
  preferred_username: string;

  realm_access: {
    roles: string[];
  };
  resource_access: {
    [name: string]: {
      roles: string[];
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _currentUser: UserDataResult | undefined;
  private _roles: string[] = [];

  private _authenticationResult = new BehaviorSubject<AuthStateResult>({
    isAuthenticated: false,
    isRenewProcess: false,
    validationResult: ValidationResult.NotSet,
  });

  constructor(protected oidcSecurityService: OidcSecurityService) {
    console.log('Initializing AuthService...');

    this.currentUser$.subscribe((user) => {
      this._currentUser = user;
    });
  }

  public checkAuth(): Observable<LoginResponse> {
    return this.oidcSecurityService.checkAuth();
  }

  public get currentUser$(): Observable<UserDataResult> {
    return this.oidcSecurityService.userData$;
  }

  public get authenticationResult$(): Observable<AuthStateResult> {
    return this._authenticationResult.asObservable();
  }

  public setAuthenticationResult(result: AuthStateResult) {
    console.log('>> Authentication result: %o', result);
    this._authenticationResult.next(result);
  }

  public setUserData(userData: KeycloakTokenDecoded) {
    console.log('>> Set user data: %o', userData);

    if (!userData) return;

    const realmRoles = userData?.realm_access?.roles || [];
    const resourceRoles = userData.resource_access
      ? Object.values(userData.resource_access)
          .map((resource) => resource.roles)
          .reduce((acc, val) => acc.concat(val), [])
      : [];
    this.roles = [...realmRoles, ...resourceRoles];
  }

  /**
   * POZOR!!! nemusi byt aktualna hodnota a v skorych fazach inicializacie nemusi byt vobec naplnena
   */
  public get currentUser(): UserDataResult | undefined {
    return this._currentUser;
  }

  public get currentUserUUID(): string {
    return this._currentUser?.userData.uid;
  }

  public get roles(): string[] {
    return this._roles;
  }

  set roles(roles: string[]) {
    this._roles = roles;
  }

  /**
   * Has any of required roles
   * @param requiredRoles
   * @returns
   */
  public hasRoles(requiredRoles: string[] | undefined): boolean {
    const roles = this.roles;

    // with requiredRoles = [] we still may require logged in user ?
    // vyzadovat prihlaseneho pouzivatela

    if (!requiredRoles || requiredRoles.length === 0) {
      // console.log('Required roles are empty');
      return true;
    } else {
      let granted = false;

      if (roles) {
        for (const requiredRole of requiredRoles) {
          if (roles.indexOf(requiredRole) > -1) {
            // console.log('Found role: ', requiredRole);
            granted = true;
            break;
          }
        }
      }

      // console.log('Required roles:', requiredRoles, 'Available: ', roles);

      return granted;
    }
  }

  /**
   * @param locale jazyk, v ktorom sa ma zobrazit prihlasovacia stranka Keycloaku (OIDC `ui_locales`).
   *   Bez neho si Keycloak vyberie jazyk podla hlavicky Accept-Language prehliadaca, takze
   *   slovenskemu prehliadacu sa prihlasenie zobrazi po slovensky aj v anglickej verzii aplikacie.
   */
  login(locale?: string): void {
    this.oidcSecurityService.authorize(undefined, locale ? { customParams: { ui_locales: locale } } : undefined);
  }

  logout(): void {
    this.oidcSecurityService.logoff();
  }
}
